import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { SupportedCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { TokenPaymentMethod } from '@sorare/core/src/__generated__/globalTypes';
import { availableBalancesFragment } from '@sorare/core/src/contexts/currentUser/queries';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { sendSafeError } from '@sorare/core/src/lib/error';
import { formatGqlErrors } from '@sorare/core/src/lib/gql';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { SmallUser } from 'components/user/SmallUser';
import { buildApplePayApprovals } from 'lib/buildApplePayApprovals';

import type {
  AcceptOfferMutation,
  AcceptOfferMutationVariables,
  useAcceptOffer_anyCard,
} from './__generated__/useAcceptOffer.graphql';
import { useApproveMigrator } from './useApproveMigrator';
import { useMigrateCards } from './useMigrateCards';
import { usePrepareAcceptOffer } from './usePrepareAcceptOffer';

const ACCEPT_OFFER_MUTATION = gql`
  mutation AcceptOfferMutation($input: acceptOfferInput!) {
    acceptOffer(input: $input) {
      deal {
        ... on TokenPrimaryOffer {
          id
          status
          endDate
          buyer {
            slug
            ...SmallUser_user
          }
          cards: anyCards {
            slug
            tokenOwner {
              id
              from
              transferType
              user {
                slug
              }
            }
          }
        }
        ... on TokenOffer {
          id
          status
          senderSide {
            id
            amounts {
              ...MonetaryAmountFragment_monetaryAmount
            }
            cards: anyCards {
              slug
              tokenOwner {
                id
                from
                transferType
              }
              liveSingleSaleOffer {
                id
              }
            }
          }
        }
        ... on TokenAuction {
          id
        }
      }
      currentUser {
        slug
        ...CurrentUserProvider_availableBalances
      }
      errors {
        path
        message
        code
      }
    }
  }
  ${monetaryAmountFragment}
  ${SmallUser.fragments.user}
  ${availableBalancesFragment}
` as TypedDocumentNode<AcceptOfferMutation, AcceptOfferMutationVariables>;

type AcceptOfferArgs = {
  supportedCurrency: SupportedCurrency;
  offerId?: string;
  cartId?: string;
  purchasableItemId?: string;
  conversionCreditIds: string[];
  receiveCards?: useAcceptOffer_anyCard[] | null;
  attemptReference: string | null;
  signedAmount?: string;
  tokenPaymentMethod?: TokenPaymentMethod;
  applePayPaymentToken?: ApplePayJS.ApplePayPaymentToken;
  afterPrepareAcceptOffer?: (offerId: string) => void;
};

export const useAcceptOffer = () => {
  const [acceptOffer] = useMutation(ACCEPT_OFFER_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const { showNotification } = useSnackNotificationContext();
  const approveMigrator = useApproveMigrator();
  const migrateCards = useMigrateCards();
  const { prepareAcceptOffer } = usePrepareAcceptOffer();
  const { track } = useEventsContext();

  const prepareAndAcceptOffer = async ({
    offerId,
    cartId,
    purchasableItemId,
    receiveCards: receiveCardsInput,
    conversionCreditIds,
    supportedCurrency,
    attemptReference,
    signedAmount,
    tokenPaymentMethod = TokenPaymentMethod.WALLET,
    applePayPaymentToken,
    afterPrepareAcceptOffer,
  }: AcceptOfferArgs) => {
    const receiveCards = receiveCardsInput || [];
    const isApplePay = tokenPaymentMethod === TokenPaymentMethod.APPLE_PAY;
    const { settlementInfo, approvals, authorizations, primaryOfferId } =
      await prepareAcceptOffer({
        offerId,
        cartId,
        purchasableItemId,
        supportedCurrency,
        tokenPaymentMethod,
        conversionCreditIds,
        attemptReference,
        signedAmount,
        signAuthorizations: !isApplePay,
      });

    const cardIds = receiveCards
      .map(card => ('id' in card ? card.id : undefined))
      .filter(Boolean);
    afterPrepareAcceptOffer?.(offerId || primaryOfferId || '');
    if (isApplePay) {
      if (!applePayPaymentToken) {
        sendSafeError(
          'Missing applePayPaymentToken for ApplePay payment method'
        );
        return null;
      }
      const applePayApprovals = buildApplePayApprovals({
        approvals: authorizations,
        applePayPaymentToken,
      });

      await approveMigrator(receiveCards);
      const migrationData = await migrateCards(receiveCards);

      const result = await acceptOffer({
        variables: {
          input: {
            offerId: offerId || primaryOfferId || '',
            migrationData,
            settlementInfo,
            approvals: applePayApprovals,
            signedAmount,
          },
        },
      });
      track('Accept Direct Offer', {
        offer_id: offerId,
      });
      return {
        cardIds,
        result,
      };
    }

    if (!approvals) {
      showNotification('unlockWallet');
      throw new Error('Missing password');
    }
    await approveMigrator(receiveCards);
    const migrationData = await migrateCards(receiveCards);

    const result = await acceptOffer({
      variables: {
        input: {
          offerId: offerId || primaryOfferId || '',
          migrationData,
          settlementInfo,
          approvals,
          signedAmount,
        },
      },
    });
    track('Accept Direct Offer', {
      offer_id: offerId || primaryOfferId,
      cart_id: cartId,
    });
    return {
      cardIds,
      result,
    };
  };

  return async (props: AcceptOfferArgs) => {
    const { result, cardIds } = (await prepareAndAcceptOffer(props)) || {};
    if (result?.errors && result?.errors.length > 0)
      return { errors: formatGqlErrors(result.errors) };
    if (
      result?.data?.acceptOffer?.errors &&
      result.data.acceptOffer.errors.length > 0
    )
      return { errors: formatGqlErrors(result.data.acceptOffer.errors) };
    return { deal: result?.data?.acceptOffer?.deal, cardIds };
  };
};

useAcceptOffer.fragments = {
  anyCard: gql`
    fragment useAcceptOffer_anyCard on AnyCardInterface {
      slug
      sport
      ... on Card {
        slug
        id
      }
      ...useApproveMigrator_anyCard
      ...useMigrateCards_anyCard
    }
    ${useApproveMigrator.fragments.anyCard}
    ${useMigrateCards.fragments.anyCard}
  ` as TypedDocumentNode<useAcceptOffer_anyCard>,
};
